import { useState, useRef } from 'react';
import { api } from '../lib/api';
import { Button } from './ui';

interface Photo {
  id: string;
  file_name: string;
  file_path: string;
  photo_type: string;
  caption?: string;
  is_primary: boolean;
  url: string;
}

interface PhotoGalleryProps {
  propertyId: string;
  photos: Photo[];
  onPhotosChange: () => void;
  editable?: boolean;
}

/**
 * ⭐⭐ WHY A THRESHOLD AT ALL, AND WHY THREE. Measured against his real data 2026-09-04: of the 40
 * properties behind "My Deals", **15 have no photos, 14 have one to three, and 11 have four or
 * more.** The pain is entirely in that last group - 25185 Madison Ave carries 18, which under the
 * old `grid-cols-1` stack was 3,792px, **5.4 phone screens.** But a swipe strip holding two items
 * that cannot really swipe reads as broken, and that would have been the COMMON case. So the
 * strip appears only where it earns its place.
 */
export const STRIP_THRESHOLD = 3;

const PHOTO_TYPES = [
  { value: 'exterior', label: 'Exterior' },
  { value: 'interior', label: 'Interior' },
  { value: 'aerial', label: 'Aerial' },
  { value: 'loading_dock', label: 'Loading Dock' },
  { value: 'parking', label: 'Parking' },
  { value: 'other', label: 'Other' },
];

export default function PhotoGallery({ propertyId, photos, onPhotosChange, editable = true }: PhotoGalleryProps) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
  const [photoType, setPhotoType] = useState('exterior');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    setError('');

    try {
      // Upload each file
      for (const file of Array.from(files)) {
        // Validate file type
        if (!file.type.startsWith('image/')) {
          setError('Please select image files only');
          continue;
        }

        // Validate file size (10MB)
        if (file.size > 10 * 1024 * 1024) {
          setError('Photos must be less than 10MB');
          continue;
        }

        await api.uploadPhoto(
          propertyId,
          file,
          photoType,
          undefined,
          photos.length === 0 // First photo is primary
        );
      }

      onPhotosChange();
    } catch (err: any) {
      setError(err.message || 'Failed to upload photos');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleSetPrimary = async (photoId: string) => {
    try {
      await api.setPrimaryPhoto(photoId);
      onPhotosChange();
    } catch (err: any) {
      setError(err.message || 'Failed to set primary photo');
    }
  };

  const handleDelete = async (photoId: string) => {
    if (!confirm('Are you sure you want to delete this photo?')) return;

    try {
      await api.deletePhoto(photoId);
      onPhotosChange();
      setSelectedPhoto(null);
    } catch (err: any) {
      setError(err.message || 'Failed to delete photo');
    }
  };

  const primaryPhoto = photos.find(p => p.is_primary) || photos[0];
  const otherPhotos = photos.filter(p => p.id !== primaryPhoto?.id);

  return (
    <div className="space-y-4">
      {/* Header with upload button */}
      {editable && (
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Photos</h3>
          <div className="flex items-center gap-2">
            <select
              value={photoType}
              onChange={(e) => setPhotoType(e.target.value)}
              className="text-sm rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            >
              {PHOTO_TYPES.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              size="sm"
            >
              {uploading ? 'Uploading...' : 'Add Photos'}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
          {error}
        </div>
      )}

      {/* ─── The photos ─────────────────────────────────────────────────────────
          Hero + a single horizontal strip. ⭐ ONE ROW, NOT A GRID THAT ALSO SCROLLS: a
          scrolling grid has no reading order (down-then-right, or right-then-down?), while a
          row has exactly one direction. It is also ~180px shorter. Total height is CONSTANT -
          18 photos costs what 4 does, where the old stack cost 3,792px. */}
      {photos.length === 0 ? (
        <div className="bg-gray-100 rounded-lg p-8 text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <p className="mt-2 text-gray-500">No photos yet</p>
          {editable && (
            <p className="text-sm text-gray-400 mt-1">Click "Add Photos" to upload property images</p>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {/* The hero. 200px on a phone rather than 256 - it still reads as the photograph of
              record, and the 56px comes straight off every property page. */}
          {primaryPhoto && (
            <button
              type="button"
              aria-label="Open the primary photo"
              onClick={() => setSelectedPhoto(primaryPhoto)}
              className="relative block w-full overflow-hidden rounded-lg bg-gray-100"
            >
              <img
                src={primaryPhoto.url}
                alt={primaryPhoto.caption || 'Property photo'}
                className="h-[200px] w-full object-cover md:h-80"
              />
              <span className="absolute left-2 top-2 rounded bg-primary-500 px-2 py-1 text-xs text-white">
                Primary
              </span>
              <span className="absolute right-2 top-2 rounded bg-gray-800/75 px-2 py-1 text-xs capitalize text-white">
                {primaryPhoto.photo_type.replace('_', ' ')}
              </span>
            </button>
          )}

          {otherPhotos.length > 0 && (
            <>
              {photos.length > STRIP_THRESHOLD ? (
                /* ⭐ `snap-x snap-mandatory` + `snap-start` per tile is what makes a swipe land
                   ON a photo instead of halfway between two. Native scrolling, no carousel
                   library - the browser does it, so it stays smooth on his phone. */
                <div
                  role="region"
                  aria-label={`More photos, ${otherPhotos.length} more`}
                  className="-mx-1 flex snap-x snap-mandatory gap-2 overflow-x-auto px-1 pb-1"
                >
                  {otherPhotos.map((photo) => (
                    <button
                      key={photo.id}
                      type="button"
                      data-photo-tile
                      aria-label={`Open photo: ${photo.caption || photo.photo_type.replace('_', ' ')}`}
                      onClick={() => setSelectedPhoto(photo)}
                      className="relative h-20 w-28 shrink-0 snap-start overflow-hidden rounded-md bg-gray-100"
                    >
                      <img
                        src={photo.url}
                        alt={photo.caption || 'Property photo'}
                        className="h-full w-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              ) : (
                /* Three or fewer: no strip. They just render. */
                <div className="grid grid-cols-2 gap-2">
                  {otherPhotos.map((photo) => (
                    <button
                      key={photo.id}
                      type="button"
                      aria-label={`Open photo: ${photo.caption || photo.photo_type.replace('_', ' ')}`}
                      onClick={() => setSelectedPhoto(photo)}
                      className="relative h-24 overflow-hidden rounded-md bg-gray-100"
                    >
                      <img
                        src={photo.url}
                        alt={photo.caption || 'Property photo'}
                        className="h-full w-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {/* ⭐ The depth, stated. Otherwise the only way to learn there are 18 is to swipe to
              the end and find out. */}
          <p className="text-[11.5px] text-gray-500">
            {photos.length} {photos.length === 1 ? 'photo' : 'photos'}
            {photos.length > STRIP_THRESHOLD && ' - swipe to see the rest'}
          </p>
        </div>
      )}

      {/* ─── Lightbox ───────────────────────────────────────────────────────────
          🚨 THE EDITING CONTROLS LIVE HERE NOW, AND THAT IS THE PHONE FIX. They used to sit on
          each tile as `opacity-0 group-hover:opacity-100`; **a phone has no hover**, so all 15
          of them were in the DOM and unreachable on his iPhone. A lightbox is also the right
          home for a destructive control - he is looking at the photograph he is about to
          delete, at full size, rather than at a thumbnail. */}
      {selectedPhoto && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`Photo: ${selectedPhoto.caption || selectedPhoto.photo_type.replace('_', ' ')}`}
          className="fixed inset-0 z-50 flex flex-col bg-black/90 pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]"
          onClick={() => setSelectedPhoto(null)}
        >
          <div className="flex shrink-0 items-center justify-between px-4 py-3" onClick={(e) => e.stopPropagation()}>
            <span className="text-sm text-white/80">
              {photos.findIndex((p) => p.id === selectedPhoto.id) + 1} / {photos.length}
            </span>
            <button
              type="button"
              aria-label="Close the photo"
              onClick={() => setSelectedPhoto(null)}
              className="flex h-9 w-9 items-center justify-center rounded-md text-2xl leading-none text-white hover:bg-white/10"
            >
              &times;
            </button>
          </div>

          <div className="flex min-h-0 flex-1 items-center justify-center px-2">
            <img
              src={selectedPhoto.url}
              alt={selectedPhoto.caption || 'Property photo'}
              className="max-h-full max-w-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          <div className="shrink-0 space-y-2 px-4 py-3" onClick={(e) => e.stopPropagation()}>
            {selectedPhoto.caption && (
              <p className="text-center text-sm text-white/90">{selectedPhoto.caption}</p>
            )}
            {photos.length > 1 && (
              <div className="flex items-center justify-center gap-2">
                <button
                  type="button"
                  aria-label="Previous photo"
                  onClick={() => setSelectedPhoto(photos[(photos.findIndex((p) => p.id === selectedPhoto.id) - 1 + photos.length) % photos.length])}
                  className="h-9 rounded-md border border-white/30 px-4 text-[12.5px] font-semibold text-white hover:bg-white/10"
                >
                  Previous
                </button>
                <button
                  type="button"
                  aria-label="Next photo"
                  onClick={() => setSelectedPhoto(photos[(photos.findIndex((p) => p.id === selectedPhoto.id) + 1) % photos.length])}
                  className="h-9 rounded-md border border-white/30 px-4 text-[12.5px] font-semibold text-white hover:bg-white/10"
                >
                  Next
                </button>
              </div>
            )}
            {editable && (
              <div className="flex items-center justify-center gap-2">
                {!selectedPhoto.is_primary && (
                  <button
                    type="button"
                    aria-label="Make this the primary photo"
                    onClick={() => { handleSetPrimary(selectedPhoto.id); setSelectedPhoto(null); }}
                    className="h-9 rounded-md bg-primary-500 px-4 text-[12.5px] font-semibold text-white hover:bg-primary-600"
                  >
                    Make primary
                  </button>
                )}
                <button
                  type="button"
                  aria-label="Delete this photo"
                  onClick={() => handleDelete(selectedPhoto.id)}
                  className="h-9 rounded-md border border-white/30 px-4 text-[12.5px] font-semibold text-white hover:bg-white/10"
                >
                  Delete
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
